import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  TrendingDown,
  CircleDollarSign,
  Wallet,
  Ban,
  TrendingUp,
  ClipboardCheck,
  Rocket,
  CheckCircle2,
  Frown,
  HeartCrack,
  Clock,
  Award,
  Smile,
} from "lucide-react";
import Reveal from "@/components/Reveal";

const SEM = {
  variant: "red",
  title: "Sua empresa SEM a EFORY",
  accent: "#EF4444",
  centerNote: "Passe o mouse nos itens e veja os riscos de não saber como fazer.",
  items: [
    { icon: Frown, label: "Mesmos resultados", summary: "Se frustrar tentando e colhendo sempre os mesmos resultados." },
    { icon: TrendingDown, label: "Negócio em queda", summary: "Ver seu negócio ir caindo com o decorrer dos anos." },
    { icon: HeartCrack, label: "Sem qualidade de vida", summary: "Perder qualidade de vida vivendo para apagar incêndios." },
    { icon: Wallet, label: "Dinheiro na mesa", summary: "Deixar dinheiro na mesa todo mês por falta de método." },
    { icon: Clock, label: "Tempo perdido", summary: "Perder tempo testando sozinho, na tentativa e erro." },
    { icon: Award, label: "Sem autoridade", summary: "Perder autoridade e credibilidade no mercado." },
  ],
};

const COM = {
  variant: "green",
  title: "Sua empresa COM a EFORY",
  accent: "#22C55E",
  centerNote: "Passe o mouse nos itens e veja o que muda ao crescer com a EFORY.",
  items: [
    { icon: TrendingUp, label: "Resultados que evoluem", summary: "Resultados que crescem de forma consistente, sem frustração." },
    { icon: Rocket, label: "Negócio crescendo", summary: "Seu negócio crescendo de forma sólida ano após ano." },
    { icon: Smile, label: "Mais qualidade de vida", summary: "Mais previsibilidade e tranquilidade no seu dia a dia." },
    { icon: CircleDollarSign, label: "Receita aproveitada", summary: "Captura todo o potencial de receita do seu negócio." },
    { icon: ClipboardCheck, label: "Método validado", summary: "Um caminho validado, sem perder tempo no achismo." },
    { icon: Award, label: "Autoridade no mercado", summary: "Constrói autoridade e credibilidade que atraem clientes." },
  ],
};

function Wheel({ data }) {
  const [active, setActive] = useState(null);
  const n = data.items.length;
  const radius = 138;
  const isRed = data.variant === "red";

  return (
    <div className="flex flex-col items-center">
      <div className="relative h-[340px] w-[340px] sm:h-[380px] sm:w-[380px]">
        {/* connecting ring */}
        <div
          className="absolute inset-10 rounded-full border-2 border-dashed"
          style={{ borderColor: `${data.accent}33` }}
        />
        {/* center */}
        <div className="absolute left-1/2 top-1/2 h-[150px] w-[150px] sm:h-[168px] sm:w-[168px] -translate-x-1/2 -translate-y-1/2">
          <div
            className="h-full w-full rounded-full flex flex-col items-center justify-center text-center px-5 border"
            style={{
              background: `radial-gradient(circle at 50% 35%, ${data.accent}26, rgba(5,5,5,0.9))`,
              borderColor: `${data.accent}55`,
            }}
          >
            {active === null ? (
              <>
                <span
                  className="text-[11px] font-bold uppercase tracking-[0.15em]"
                  style={{ color: data.accent }}
                >
                  {isRed ? "Sem a EFORY" : "Com a EFORY"}
                </span>
                <p className="text-[11px] text-white/55 mt-1.5 leading-snug">{data.centerNote}</p>
              </>
            ) : (
              <motion.div
                key={active}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25 }}
              >
                <span className="text-sm font-bold text-white block leading-tight">
                  {data.items[active].label}
                </span>
                <p className="text-[11px] text-white/70 mt-1.5 leading-snug">
                  {data.items[active].summary}
                </p>
              </motion.div>
            )}
          </div>
        </div>

        {/* items around */}
        {data.items.map((item, i) => {
          const angle = (-90 + i * (360 / n)) * (Math.PI / 180);
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius;
          const Icon = item.icon;
          const isActive = active === i;
          return (
            <div
              key={item.label}
              className="absolute left-1/2 top-1/2"
              style={{ transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))` }}
            >
              <motion.button
                data-testid={`wheel-${data.variant}-item-${i}`}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onMouseLeave={() => setActive(null)}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.12 }}
                transition={{ delay: 0.05 * i, type: "spring", stiffness: 200, damping: 16 }}
                className="flex h-[72px] w-[72px] flex-col items-center justify-center gap-1 rounded-2xl border text-center"
                style={{
                  background: isActive ? data.accent : "rgba(255,255,255,0.05)",
                  borderColor: isActive ? data.accent : "rgba(255,255,255,0.14)",
                }}
              >
                <Icon className="h-5 w-5" style={{ color: isActive ? "#fff" : data.accent }} />
                <span className={`text-[8.5px] font-semibold leading-tight px-1 ${isActive ? "text-white" : "text-white/65"}`}>
                  {item.label}
                </span>
              </motion.button>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex items-center gap-2">
        {isRed ? (
          <Ban className="h-5 w-5" style={{ color: data.accent }} />
        ) : (
          <CheckCircle2 className="h-5 w-5" style={{ color: data.accent }} />
        )}
        <h3 className="font-heading text-lg sm:text-xl font-bold text-white">{data.title}</h3>
      </div>
    </div>
  );
}

export default function MarketingWheel() {
  return (
    <section id="comparativo" className="scroll-mt-nav bg-efory-ink text-white py-20 md:py-28 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <Reveal className="text-center max-w-3xl mx-auto">
          <span className="inline-block rounded-full border border-efory-blue/30 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-efory-blue">
            O contraste é claro
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-6 leading-[1.1]">
            Com método, tudo <span className="text-efory-blue">muda de figura</span>
          </h2>
          <p className="mt-5 text-white/65 text-base sm:text-lg">
            Veja a diferença entre crescer no improviso e crescer com a estrutura da EFORY.
          </p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-12 lg:gap-8 items-start">
          <Reveal delay={0.05}>
            <Wheel data={SEM} />
          </Reveal>
          <Reveal delay={0.15}>
            <Wheel data={COM} />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
